import { useCallback, useEffect, useRef, useState } from "react"

import {
  loadPanelWidth,
  MAX_PANEL_WIDTH_FRACTION,
  MIN_PANEL_WIDTH,
  savePanelWidth,
} from "../utils/storage"

/** Clamp a desired docked width to [MIN, 66% of the viewport]. */
function clampPanelWidth(width: number): number {
  const max =
    typeof window !== "undefined"
      ? Math.max(
          MIN_PANEL_WIDTH,
          Math.round(window.innerWidth * MAX_PANEL_WIDTH_FRACTION),
        )
      : Number.POSITIVE_INFINITY
  return Math.min(Math.max(width, MIN_PANEL_WIDTH), max)
}

interface PanelWidthState {
  /** Docked-panel width in px, always clamped to the current viewport. */
  width: number
  /** Widest the panel can be in this viewport; unknown until mounted. */
  maxWidth?: number
  /** Set the docked width (clamped + persisted once the drag settles). */
  setWidth: (width: number) => void
  /** True while the user is dragging — suppresses the reflow transition. */
  isResizing: boolean
  setResizing: (resizing: boolean) => void
}

/**
 * Owns the docked panel's width: the opening width, the persisted preference,
 * viewport clamping, and the drag lifecycle.
 *
 * The panel opens at its narrowest and widens only when the user drags it
 * there. A width the user has actually stored wins over that opening width.
 */
export function usePanelWidth(): PanelWidthState {
  // Starts at the opening width for SSR parity, then hydrates the persisted
  // preference post-mount.
  const [width, setWidthState] = useState(MIN_PANEL_WIDTH)
  const [isResizing, setResizing] = useState(false)
  const [maxWidth, setMaxWidth] = useState<number | undefined>()
  const widthDirtyRef = useRef(false)

  useEffect(() => {
    setMaxWidth(clampPanelWidth(Number.POSITIVE_INFINITY))
    const stored = loadPanelWidth()
    // A stored width always wins; otherwise clamp the opening width to the
    // viewport this session actually has.
    setWidthState(
      stored != null
        ? clampPanelWidth(stored)
        : clampPanelWidth(MIN_PANEL_WIDTH),
    )
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setMaxWidth(clampPanelWidth(Number.POSITIVE_INFINITY))
      setWidthState((current) => {
        const clamped = clampPanelWidth(current)
        if (clamped !== current) {
          savePanelWidth(clamped)
        }
        return clamped
      })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const setWidth = useCallback((next: number) => {
    widthDirtyRef.current = true
    setWidthState(clampPanelWidth(next))
  }, [])

  // Persist only once the drag settles, so a drag writes one value rather than
  // one per pointer move.
  useEffect(() => {
    if (!widthDirtyRef.current || isResizing) {
      return
    }
    savePanelWidth(width)
    widthDirtyRef.current = false
  }, [isResizing, width])

  return { width, maxWidth, setWidth, isResizing, setResizing }
}
